(function () {
  function getTablist(root) {
    return root.querySelector('.featured-collection-tabs__list[role="tablist"]');
  }

  function updateTablistIndicator(root) {
    const tablist = getTablist(root);
    if (!tablist) return;

    const activeTab = tablist.querySelector('.featured-collection-tabs__tab[aria-selected="true"]');
    if (!activeTab) {
      tablist.style.setProperty('--left-offset', '0px');
      tablist.style.setProperty('--width', '0px');
      return;
    }

    const listRect = tablist.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();
    const left = tabRect.left - listRect.left + tablist.scrollLeft;
    const width = tabRect.width;

    tablist.style.setProperty('--left-offset', `${Math.max(0, left)}px`);
    tablist.style.setProperty('--width', `${Math.max(0, width)}px`);
  }

  function scheduleIndicatorUpdate(root) {
    requestAnimationFrame(() => {
      updateTablistIndicator(root);
      requestAnimationFrame(() => updateTablistIndicator(root));
    });
  }

  function bindIndicatorLayout(root) {
    const tablist = getTablist(root);
    if (!tablist || tablist.dataset.indicatorLayoutBound === 'true') return;
    tablist.dataset.indicatorLayoutBound = 'true';

    const onLayout = () => updateTablistIndicator(root);

    const ro = new ResizeObserver(onLayout);
    ro.observe(tablist);
    tablist.querySelectorAll('.featured-collection-tabs__tab').forEach((tab) => ro.observe(tab));

    window.addEventListener('resize', onLayout);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(onLayout);
    }
  }

  function selectTab(root, tab) {
    const panelId = tab.getAttribute('aria-controls');
    if (!panelId) return;

    const tabs = [...root.querySelectorAll('[role="tab"]')];
    tabs.forEach((t) => {
      const selected = t === tab;
      t.setAttribute('aria-selected', String(selected));
      t.tabIndex = selected ? 0 : -1;
    });

    root.querySelectorAll('[role="tabpanel"]').forEach((panel) => {
      const active = panel.id === panelId;
      panel.toggleAttribute('hidden', !active);
      if (active) {
        const slider = panel.querySelector('theme-slider');
        requestAnimationFrame(() => {
          slider?.swiperInstance?.update?.();
          slider?.swiperInstance?.updateSize?.();
        });
      }
    });

    const blockId = tab.dataset.blockId;
    if (blockId) {
      const viewSlots = root.querySelectorAll('[data-view-all-for]');
      if (viewSlots.length) {
        let matched = false;
        viewSlots.forEach((slot) => {
          const isMatch = slot.dataset.viewAllFor === blockId;
          if (isMatch) matched = true;
          slot.toggleAttribute('hidden', !isMatch);
        });
        if (!matched) {
          viewSlots.forEach((slot) => slot.setAttribute('hidden', ''));
        }
      }
    }

    scheduleIndicatorUpdate(root);
  }

  function initFeaturedCollectionTabs(root) {
    if (!root || root.dataset.tabsBound === 'true') return;
    root.dataset.tabsBound = 'true';

    bindIndicatorLayout(root);

    root.addEventListener('click', (event) => {
      const tab = event.target.closest('[role="tab"]');
      if (!tab || !root.contains(tab)) return;
      selectTab(root, tab);
    });

    scheduleIndicatorUpdate(root);
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-featured-collection-tabs]').forEach(initFeaturedCollectionTabs);
  });

  document.addEventListener('shopify:section:load', (event) => {
    event.target?.querySelectorAll?.('[data-featured-collection-tabs]').forEach(initFeaturedCollectionTabs);
  });
})();

class PromoVideoPlayer extends HTMLElement {
  connectedCallback() {
    this._video = this.querySelector('video');
    if (!this._video) return;

    this._overlay = this.querySelector('.promo-video__overlay');
    this._playBtn = this.querySelector('.promo-video__play-btn');
    this._muteBtn = this.querySelector('.promo-video__mute-btn');
    this._slider = this.querySelector('.promo-video__volume-slider');

    // Start muted; preserve intended volume for when user unmutes
    this._video.volume = 0.8;
    this._syncMuteIcon();

    // Clicking the video or overlay toggles play/pause
    this._video.addEventListener('click', () => this._togglePlay());
    this._overlay.addEventListener('click', () => this._togglePlay());

    // Play button (shown when paused, centered over video)
    if (this._playBtn) {
      this._playBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._togglePlay();
      });
    }

    // Mute button only toggles mute — does not affect play state
    if (this._muteBtn) {
      this._muteBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._toggleMute();
      });
    }

    // Volume slider sets volume and auto-unmutes if dragged above 0
    if (this._slider) {
      this._slider.addEventListener('input', () => this._onVolumeInput());
    }

    // Sync paused/playing state
    this._video.addEventListener('play', () => this._setPausedState(false));
    this._video.addEventListener('pause', () => this._setPausedState(true));
  }

  _togglePlay() {
    if (this._video.paused) {
      this._video.play();
    } else {
      this._video.pause();
    }
  }

  _toggleMute() {
    this._video.muted = !this._video.muted;
    // If volume was at 0 and user unmutes, restore a sensible level
    if (!this._video.muted && this._video.volume === 0) {
      this._video.volume = 0.8;
      this._slider.value = 0.8;
    }
    this._syncMuteIcon();
  }

  _onVolumeInput() {
    const value = parseFloat(this._slider.value);
    this._video.volume = value;
    this._video.muted = value === 0;
    this._syncMuteIcon();
  }

  _setPausedState(isPaused) {
    this.classList.toggle('is-paused', isPaused);
    if (this._playBtn) this._playBtn.hidden = !isPaused;
  }

  _syncMuteIcon() {
    if (!this._muteBtn) return;
    const isMuted = this._video.muted || this._video.volume === 0;
    this._muteBtn.querySelector('.icon--muted').hidden = !isMuted;
    if (isMuted) {
      this._muteBtn.querySelector('.icon--muted').classList.remove('hidden');
      this._muteBtn.querySelector('.icon--sound').classList.add('hidden');
    } else {
      this._muteBtn.querySelector('.icon--muted').classList.add('hidden');
      this._muteBtn.querySelector('.icon--sound').classList.remove('hidden');
    }
    this._muteBtn.setAttribute('aria-label', isMuted ? 'Unmute video' : 'Mute video');
  }
}

if (!customElements.get('promo-video-player')) {
  customElements.define('promo-video-player', PromoVideoPlayer);
}